import React from 'react'
import { useI18n } from '../../i18n'
import { featureFlags } from '../../config/featureFlags'
import { GovernanceListingSection } from '../governance/GovernanceListingSection'
import WorkflowReadinessSummary from '../compliance-readiness/WorkflowReadinessSummary'
import ReadinessGapList from '../compliance-readiness/ReadinessGapList'
import wfStyles from '@/styles/pages/compliance-workflow/ComplianceWorkflow.module.scss'

interface Props {
  readiness?: {
    systems?: Array<{
      informationSystemId: string
      readiness?: {
        readinessStatus?: string
        evidenceCoverage?: number | null
        operationalPressure?: number | null
        explanations?: Array<{
          message: string
          type?: string
          canonicalControlKey?: string
          soc2Mapping?: string
          missingEvidenceTypes?: string[]
        }>
      }
      progress?: {
        satisfiedControls?: number
        totalControls?: number
        controlsSatisfiedLabel?: string
      }
      evidenceGaps?: Array<{
        canonicalControlKey?: string
        controlTitle?: string | null
        missing?: string[]
        reason?: string
      }>
    }>
    standardReadiness?: { readinessStatus?: string }
    materializedEnabled?: boolean
    syncEnabled?: boolean
    message?: string
  } | null
  standardCode?: string
}

export default function TopologyReadinessWorkspace({ readiness, standardCode }: Props) {
  const { t } = useI18n()
  const canonicalV1 = featureFlags.CANONICAL_CONTROL_LIBRARY_V1
  if (!readiness) return null
  if (!readiness.materializedEnabled) {
    return (
      <GovernanceListingSection title={t('complianceWorkflow.readinessBaseline', 'Readiness Baseline')}>
        <p className={wfStyles.loadingText}>
          {readiness.message ||
            t('complianceTopology.scopedSummaryUnavailable', 'Enable COMPLIANCE_TOPOLOGY_READINESS_V1 and finalize scope.')}
        </p>
      </GovernanceListingSection>
    )
  }
  if (!(readiness.systems || []).length) {
    return (
      <GovernanceListingSection title={t('complianceWorkflow.readinessBaseline', 'Readiness Baseline')}>
        <p className={wfStyles.loadingText}>
          {t('complianceWorkflow.runDiscoveryHint', 'Finalize scope and run discovery to establish readiness baseline.')}
        </p>
      </GovernanceListingSection>
    )
  }
  const syncV1 = featureFlags.COMPLIANCE_READINESS_SYNC_V1 && readiness.syncEnabled

  return (
    <GovernanceListingSection title={t('complianceWorkflow.readinessBaseline', 'Readiness Baseline')}>
      {syncV1 ? (
        <WorkflowReadinessSummary
          systems={readiness.systems}
          standardReadiness={readiness.standardReadiness}
        />
      ) : null}
      {!syncV1 ? (
        <ul className={wfStyles.innerStack}>
          {(readiness.systems || []).map((s) => (
            <li key={s.informationSystemId} className={wfStyles.listItem}>
              <p className={wfStyles.itemTitle}>
                <span className={wfStyles.monoId}>{s.informationSystemId.slice(0, 8)}</span>
                {s.readiness ? (
                  <span className="ml-2">{s.readiness.readinessStatus}</span>
                ) : null}
              </p>
              {s.readiness?.evidenceCoverage != null ? (
                <p className={wfStyles.itemMeta}>
                  {t('complianceTopology.evidenceCoverage', 'Evidence coverage')}: {s.readiness.evidenceCoverage}%
                </p>
              ) : null}
              {(s.readiness?.explanations || []).slice(0, canonicalV1 ? 8 : 3).map((ex, i) => (
                <p key={i} className={wfStyles.itemMeta}>
                  {ex.canonicalControlKey ? (
                    <span>
                      <strong>{ex.canonicalControlKey}</strong>
                      {ex.soc2Mapping ? ` (${ex.soc2Mapping})` : ''}: {ex.message}
                      {ex.missingEvidenceTypes?.length ? (
                        <span>
                          {' '}
                          — {t('canonicalLibrary.expectedEvidence', 'Expected evidence')}:{' '}
                          {ex.missingEvidenceTypes.slice(0, 3).join(', ')}
                        </span>
                      ) : null}
                    </span>
                  ) : (
                    ex.message
                  )}
                </p>
              ))}
            </li>
          ))}
        </ul>
      ) : null}
      {syncV1 ? (
        <>
          {(readiness.systems || []).map((s) => (
            <div key={s.informationSystemId}>
              {(s.readiness?.explanations || []).slice(0, 5).map((ex, i) => (
                <p key={i} className={wfStyles.itemMeta}>
                  {ex.canonicalControlKey ? (
                    <span>
                      <strong>{ex.canonicalControlKey}</strong>: {ex.message}
                    </span>
                  ) : (
                    ex.message
                  )}
                </p>
              ))}
              <ReadinessGapList gaps={s.evidenceGaps} />
            </div>
          ))}
        </>
      ) : null}
      {standardCode && syncV1 ? (
        <p className={wfStyles.itemMeta}>
          {t('complianceReadiness.syncActive', 'Unified readiness sync active for {{code}}', {
            code: standardCode,
          })}
        </p>
      ) : null}
    </GovernanceListingSection>
  )
}
